import React, {useCallback, useEffect, useState} from "react";
import {Button, Form, Input, message, Typography, Spin, Checkbox} from "antd";
import {ExclamationCircleOutlined, MailOutlined, UserOutlined} from "@ant-design/icons";
import {Link, useSearchParams} from "react-router-dom";
import {useCreateOrganisationWithUserMutation, useGetSignupConfigQuery, useGetQualificationConfigQuery, useVerifyWaitlistInviteQuery} from "../services/nestjs-base-api";
import {ICreateOrganisationWithApiUser} from "../services/nestjs-base-api/types";
import { useIntl } from "react-intl";
import ModernAuthLayout from "../components/Auth/ModernAuthLayout";
import { QualificationStep, QualificationAnswers, ResolvedQualification } from "../components/Auth/QualificationStep";
import { WaitlistStep } from "../components/Auth/WaitlistStep";

const { Text, Title } = Typography;

type SignupStep = 'qualification' | 'waitlist' | 'register';

interface QualifiedData {
    brandName: string;
    websiteUrl?: string;
    productCategory?: string;
    businessModel?: string;
    templateType: string;
}

interface WaitlistData {
    brandName: string;
    websiteUrl?: string;
    productCategory?: string;
    businessModel?: string;
}

const SignupPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const [isMobile, setIsMobile] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [hideForm, setHideForm] = useState<boolean>(false);
    const [register, registerResult] = useCreateOrganisationWithUserMutation();
    const { data: signupConfig, isLoading: isLoadingConfig } = useGetSignupConfigQuery();
    const { data: qualificationConfig, isLoading: isLoadingQualification } = useGetQualificationConfigQuery();

    // SYR-109: a signed waitlist invite opens the closed-beta door. The same
    // token travels with the register call, because the API now enforces the
    // closed state itself and the client-side check alone would be theatre.
    const inviteTokenParam = searchParams.get('inviteToken');
    const { data: inviteVerify, isLoading: isVerifyingInvite } = useVerifyWaitlistInviteQuery(
        { token: inviteTokenParam || '' },
        { skip: !inviteTokenParam },
    );
    const hasValidInvite = inviteVerify?.valid === true;

    const [step, setStep] = useState<SignupStep>('qualification');
    const [qualifiedData, setQualifiedData] = useState<QualifiedData | null>(null);
    const [waitlistData, setWaitlistData] = useState<WaitlistData | null>(null);
    const [urlPrefill, setUrlPrefill] = useState<{
        firstName?: string;
        lastName?: string;
        email?: string;
        businessName?: string;
        websiteUrl?: string;
    }>({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Handle URL prefill (from waitlist invite emails)
    useEffect(() => {
        const firstName = searchParams.get('firstName') || undefined;
        const lastName = searchParams.get('lastName') || undefined;
        const email = searchParams.get('email') || undefined;
        const businessName = searchParams.get('businessName') || undefined;
        const websiteUrl = searchParams.get('websiteUrl') || undefined;
        setUrlPrefill({ firstName, lastName, email, businessName, websiteUrl });

        // Bypass qualification wizard for invited waitlist users. The server
        // derives productType from templateType (registry), so we only need the
        // templateType from the invite link.
        const templateType = searchParams.get('templateType');
        if (templateType && businessName) {
            setQualifiedData({
                brandName: businessName,
                websiteUrl,
                templateType,
            });
            setStep('register');
        }
    }, [searchParams]);

    const onRegisterError = useCallback(() => {
        messageApi.open({
            type: 'error',
            content: intl.formatMessage({ id: 'auth.signup.registrationError' }),
            duration: 6,
            icon: <ExclamationCircleOutlined />
        });
    }, [messageApi, intl]);

    const handleRegister = useCallback(async (values: { firstName: string; lastName: string; email: string; acceptTerms?: boolean }) => {
        if (!qualifiedData) return;

        const payload: ICreateOrganisationWithApiUser = {
            organisationName: qualifiedData.brandName,
            websiteUrl: qualifiedData.websiteUrl,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            acceptTerms: values.acceptTerms,
            inviteToken: inviteTokenParam || undefined,
            productCategory: qualifiedData.productCategory,
            businessModel: qualifiedData.businessModel,
            templateType: qualifiedData.templateType,
        };

        const registerResponse = await register({payload: payload});

        if ('data' in registerResponse) {
            setHideForm(true);
        } else {
            onRegisterError();
        }
    }, [onRegisterError, register, qualifiedData]);

    const handleQualified = useCallback((answers: QualificationAnswers, resolved: ResolvedQualification) => {
        setQualifiedData({
            brandName: answers.brandName,
            websiteUrl: answers.websiteUrl,
            productCategory: resolved.productCategory,
            businessModel: resolved.businessModel,
            templateType: resolved.templateType,
        });
        setStep('register');
    }, []);

    const handleNotQualified = useCallback((answers: QualificationAnswers, productCategory?: string, businessModel?: string) => {
        setWaitlistData({
            brandName: answers.brandName,
            websiteUrl: answers.websiteUrl,
            productCategory,
            businessModel,
        });
        setStep('waitlist');
    }, []);

    // Inline SVG logo component for crisp rendering
    const LogoIcon = ({ size = 48 }: { size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke="#3B82F6" strokeWidth="2" opacity="0.3"/>
            <circle cx="24" cy="24" r="10" fill="#F59E0B"/>
            <circle cx="24" cy="4" r="4" fill="#3B82F6"/>
            <circle cx="6" cy="34" r="4" fill="#22C55E"/>
            <circle cx="42" cy="34" r="4" fill="#3B82F6"/>
        </svg>
    );

    const signupLeftContent = (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '2rem' }}>
                <LogoIcon size={48} />
                <span style={{ fontSize: '32px', fontWeight: '700' }}>
                    <span style={{ color: 'white' }}>BrandScience</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>.ai</span>
                </span>
            </div>
            <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', lineHeight: '1.2' }}>
                {intl.formatMessage({ id: 'auth.signup.heading' })}
            </h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.9, lineHeight: '1.6', marginBottom: '2rem' }}>
                {intl.formatMessage({ id: 'auth.signup.description' })}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                    <span>{intl.formatMessage({ id: 'auth.signup.feature.tracking' })}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                    <span>{intl.formatMessage({ id: 'auth.signup.feature.analytics' })}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                    <span>{intl.formatMessage({ id: 'auth.signup.feature.visualizations' })}</span>
                </div>
            </div>
        </div>
    );

    // Check if public signup is enabled (from system setting). A verified
    // waitlist invite renders the form through the closed-beta wall.
    const isClosedBeta = !signupConfig?.publicSignupEnabled && !hasValidInvite;
    const isLoading = isLoadingConfig || isLoadingQualification || (!!inviteTokenParam && isVerifyingInvite);

    // SYR-109: the register-step subtitle tells the truth about what this
    // signup IS under the current pricing flags. Missing fields read as
    // today's defaults, so an older API keeps today's copy.
    const welcomeTextId = signupConfig?.freeTierSignupEnabled !== false
        ? 'auth.signup.welcomeText'
        : signupConfig?.freeTrialEnabled !== false
            ? 'auth.signup.welcomeTextTrial'
            : 'auth.signup.welcomeTextPaid';

    return (
        <ModernAuthLayout leftContent={signupLeftContent}>
            {contextHolder}
            {/* SYR-109: while the Charter intake runs alongside signup, the
                form offers the one-to-one door without interrupting anyone
                already typing. Hidden on the waitlist step (they did not
                qualify for self-serve, so the cohort pitch reads as a tease)
                and on the closed-beta panel, which has its own Charter line. */}
            {!isLoading && !isClosedBeta && !hideForm && step !== 'waitlist' && signupConfig?.charterCohortEnabled && (
                <Link to="/charter" style={{ display: 'block', textDecoration: 'none', marginBottom: isMobile ? '1.25rem' : '1.5rem' }}>
                    <div style={{
                        background: '#0F172A',
                        border: '1px solid rgba(245, 158, 11, 0.45)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        textAlign: 'center',
                    }}>
                        <Text style={{ color: 'rgba(255,255,255,0.88)', fontSize: isMobile ? '13px' : '14px' }}>
                            {intl.formatMessage({ id: 'auth.signup.charterBanner' })}
                        </Text>
                        {' '}
                        <Text style={{ color: '#F59E0B', fontWeight: 600, fontSize: isMobile ? '13px' : '14px', whiteSpace: 'nowrap' }}>
                            {intl.formatMessage({ id: 'auth.signup.charterBannerLink' })} {'\u2192'}
                        </Text>
                    </div>
                </Link>
            )}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                    <Spin size="large" />
                </div>
            ) : isClosedBeta ? (
                // Closed Beta Message
                <div style={{ textAlign: 'center', padding: isMobile ? '2rem 1rem' : '3rem 2rem' }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 2rem',
                        boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)'
                    }}>
                        <MailOutlined style={{ fontSize: '40px', color: 'white' }} />
                    </div>

                    <Title level={2} style={{
                        color: '#1f2937',
                        fontWeight: '700',
                        marginBottom: '1rem',
                        fontSize: isMobile ? '24px' : '32px'
                    }}>
                        Invitation-Only Closed Beta
                    </Title>

                    <Text style={{
                        color: '#6b7280',
                        fontSize: isMobile ? '15px' : '17px',
                        lineHeight: '1.7',
                        display: 'block',
                        marginBottom: '2rem'
                    }}>
                        We're currently in a closed beta phase with our founding cohort.
                        BrandScience.ai is being refined based on early feedback to ensure we deliver
                        the best possible brand equity measurement experience.
                    </Text>

                    <div style={{
                        background: '#f9fafb',
                        border: '2px solid #e5e7eb',
                        borderRadius: '16px',
                        padding: isMobile ? '1.5rem' : '2rem',
                        marginBottom: '2rem'
                    }}>
                        <Title level={4} style={{
                            color: '#1f2937',
                            marginBottom: '1rem',
                            fontSize: isMobile ? '16px' : '18px'
                        }}>
                            Interested in joining?
                        </Title>
                        <Text style={{
                            color: '#6b7280',
                            fontSize: isMobile ? '14px' : '16px',
                            display: 'block',
                            marginBottom: '1rem'
                        }}>
                            We'd love to hear from you! Send us an email to join our waitlist
                            and be among the first to know when we open up.
                        </Text>
                        <Button
                            type="primary"
                            size="large"
                            icon={<MailOutlined />}
                            href="mailto:hello@brandscience.ai?subject=BrandScience.ai Waitlist"
                            style={{
                                height: isMobile ? '52px' : '48px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                border: 'none',
                                fontSize: isMobile ? '15px' : '16px',
                                fontWeight: '600',
                                boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.4)',
                                paddingLeft: '32px',
                                paddingRight: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            Email hello@brandscience.ai
                        </Button>
                    </div>

                    {signupConfig?.charterCohortEnabled && (
                        <div style={{ marginBottom: '1rem' }}>
                            <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                                {intl.formatMessage({ id: 'auth.signup.charterOpenNote' })}
                            </Text>
                            {" "}
                            <Link to="/charter" style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px', textDecoration: 'none' }}>
                                {intl.formatMessage({ id: 'auth.signup.charterOpenLink' })}
                            </Link>
                        </div>
                    )}

                    <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                        <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                            Already have an account?
                        </Text>
                        {" "}
                        <Link to="/sign-in" style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px', textDecoration: 'none' }}>
                            {intl.formatMessage({ id: 'auth.signup.signinLink' })}
                        </Link>
                    </div>
                </div>
            ) : step === 'qualification' ? (
                <QualificationStep
                    isMobile={isMobile}
                    config={qualificationConfig}
                    initialBrandName={urlPrefill.businessName}
                    initialWebsiteUrl={urlPrefill.websiteUrl}
                    onQualified={handleQualified}
                    onNotQualified={handleNotQualified}
                />
            ) : step === 'waitlist' && waitlistData ? (
                <WaitlistStep
                    brandName={waitlistData.brandName}
                    websiteUrl={waitlistData.websiteUrl}
                    productCategory={waitlistData.productCategory}
                    businessModel={waitlistData.businessModel}
                    isMobile={isMobile}
                    prefill={urlPrefill}
                />
            ) : step === 'register' && qualifiedData && !hideForm ? (
                <>
                    <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
                        <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '0.5rem', fontSize: isMobile ? '24px' : '30px' }}>
                            {intl.formatMessage({ id: 'auth.signup.title' })}
                        </Title>
                        <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                            {intl.formatMessage({ id: welcomeTextId })}
                        </Text>
                        <div style={{ marginTop: '0.75rem' }}>
                            <Text style={{ color: '#374151', fontSize: isMobile ? '14px' : '15px', fontWeight: 600 }}>
                                {qualifiedData.brandName}
                            </Text>
                        </div>
                    </div>

                    <Form
                        name="signup_form"
                        onFinish={handleRegister}
                        layout="vertical"
                        requiredMark={false}
                        size="large"
                        initialValues={{
                            firstName: urlPrefill.firstName || '',
                            lastName: urlPrefill.lastName || '',
                            email: urlPrefill.email || '',
                        }}
                    >
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Form.Item
                                name="firstName"
                                label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signup.label.firstName' })}</span>}
                                rules={[
                                    {
                                        required: true,
                                        message: intl.formatMessage({ id: 'auth.signup.firstNameRequired' }),
                                    },
                                ]}
                                style={{ flex: 1 }}
                            >
                                <Input
                                    prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                                    placeholder={intl.formatMessage({ id: 'auth.signup.firstNamePlaceholder' })}
                                    style={{
                                        height: isMobile ? '52px' : '48px',
                                        borderRadius: '12px',
                                        border: '2px solid #e5e7eb',
                                        fontSize: isMobile ? '16px' : '16px'
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="lastName"
                                label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signup.label.lastName' })}</span>}
                                rules={[
                                    {
                                        required: true,
                                        message: intl.formatMessage({ id: 'auth.signup.lastNameRequired' }),
                                    },
                                ]}
                                style={{ flex: 1 }}
                            >
                                <Input
                                    prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                                    placeholder={intl.formatMessage({ id: 'auth.signup.lastNamePlaceholder' })}
                                    style={{
                                        height: isMobile ? '52px' : '48px',
                                        borderRadius: '12px',
                                        border: '2px solid #e5e7eb',
                                        fontSize: isMobile ? '16px' : '16px'
                                    }}
                                />
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="email"
                            label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signup.label.email' })}</span>}
                            rules={[
                                {
                                    type: "email",
                                    required: true,
                                    message: intl.formatMessage({ id: 'auth.signup.emailRequired' }),
                                },
                            ]}
                        >
                            <Input
                                prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                                placeholder={intl.formatMessage({ id: 'auth.signup.emailPlaceholder' })}
                                style={{
                                    height: isMobile ? '52px' : '48px',
                                    borderRadius: '12px',
                                    border: '2px solid #e5e7eb',
                                    fontSize: isMobile ? '16px' : '16px'
                                }}
                            />
                        </Form.Item>

                        {signupConfig?.legalAcceptanceEnabled && (
                            <Form.Item
                                name="acceptTerms"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject(new Error(intl.formatMessage({ id: 'auth.signup.acceptTermsRequired' }))),
                                    },
                                ]}
                                style={{ marginBottom: '24px' }}
                            >
                                <Checkbox>
                                    <Text style={{ color: '#374151', fontSize: isMobile ? '14px' : '14px' }}>
                                        {intl.formatMessage({ id: 'auth.signup.acceptTermsPrefix' })}{' '}
                                        <Link to="/privacy-policy" target="_blank" style={{ color: '#F59E0B' }}>
                                            {intl.formatMessage({ id: 'auth.signup.privacyPolicy' })}
                                        </Link>{' '}
                                        {intl.formatMessage({ id: 'auth.signup.acceptTermsAnd' })}{' '}
                                        <Link to="/terms-of-service" target="_blank" style={{ color: '#F59E0B' }}>
                                            {intl.formatMessage({ id: 'auth.signup.termsOfService' })}
                                        </Link>
                                    </Text>
                                </Checkbox>
                            </Form.Item>
                        )}

                        <Form.Item>
                            <Button
                                block
                                type="primary"
                                htmlType="submit"
                                loading={registerResult.isLoading}
                                style={{
                                    height: isMobile ? '52px' : '48px',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                    border: 'none',
                                    fontSize: isMobile ? '16px' : '16px',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.4)'
                                }}
                            >
                                {intl.formatMessage({ id: 'auth.signup.title' })}
                            </Button>
                        </Form.Item>

                        <div style={{ textAlign: 'center', marginTop: isMobile ? '1rem' : '1.5rem' }}>
                            <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                                {intl.formatMessage({ id: 'auth.signup.hasAccount' })}
                            </Text>
                            {" "}
                            <Link to="/sign-in" style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px', textDecoration: 'none' }}>
                                {intl.formatMessage({ id: 'auth.signup.signinLink' })}
                            </Link>
                        </div>
                    </Form>
                </>
            ) : hideForm ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '1rem' }}>
                        {intl.formatMessage({ id: 'auth.signup.thankYouTitle' })}
                    </Title>
                    <Text style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                        {intl.formatMessage({ id: 'auth.signup.confirmationText' })}
                    </Text>
                </div>
            ) : null}
        </ModernAuthLayout>
    );
}

export default SignupPage;
